import React, {useCallback, useState, useMemo} from 'react'
import {useTranslation} from 'react-i18next'
import {AppointmentForPractitioner, AppointmentStatus} from '@common/models'

import {Container} from '../general/container/Container'
import {SimpleTable} from '../general/table/SimpleTable'
import {RescheduleAppointmentModal} from './RescheduleAppointmentModal'
import {CancelAppointmentModal} from './CancelAppointmentModal'
import {TableCellIconButtonPopup} from '../patients/common/patientsTable/PatientsTable.styled'
import {DNAAppointmentModal} from './DNAAppointmentModal'

import {formatName} from '~/utils'

type WebAppointmentStatus =
  | 'Did Not Attend'
  | 'Completed'
  | 'Cancelled'
  | 'Pending'
  | 'Attended'
  | 'Not Completed'

function isBeforeToday(inputDate: Date): boolean {
  const today = new Date()
  // Strip time part from today
  const todayDateOnly = new Date(
    today.getFullYear(),
    today.getMonth(),
    today.getDate(),
  )

  // Strip time part from input date
  const inputDateOnly = new Date(
    inputDate.getFullYear(),
    inputDate.getMonth(),
    inputDate.getDate(),
  )

  return inputDateOnly < todayDateOnly
}

export type PractitionerAppointmentsTableProps = {
  data: AppointmentForPractitioner[] | undefined
  isLoading: boolean
  onRowSelected?: (appointment: AppointmentForPractitioner) => void
}

export const PractitionerAppointmentsTable = ({
  data,
  isLoading,
  onRowSelected,
}: PractitionerAppointmentsTableProps): JSX.Element => {
  const {t} = useTranslation<string>()
  const [selectedAppointment, setSelectedAppointment] = useState<{
    id: string
    facilityId: string
    startTime: string
    patientName: string
    patientDob: string | null
    department: string | null
    action: 'reschedule' | 'cancel' | 'dna'
    existingNote?: string
    currentStatus?: WebAppointmentStatus
  } | null>(null)

  const statusLabels = useMemo<Record<WebAppointmentStatus, string>>(
    () => ({
      'Did Not Attend': t('Did Not Attend'),
      Completed: t('Completed'),
      Cancelled: t('Cancelled'),
      Pending: t('Pending'),
      Attended: t('Attended'),
      'Not Completed': t('Not Completed'),
    }),
    [t],
  )

  const calculateStatus = useCallback(
    (appointment: AppointmentForPractitioner): WebAppointmentStatus => {
      const appointmentStatusMap: Record<
        AppointmentStatus,
        WebAppointmentStatus | null
      > = {
        [AppointmentStatus.NOSHOW]: 'Did Not Attend',
        [AppointmentStatus.FULFILLED]: 'Completed',
        [AppointmentStatus.CANCELLED]: 'Cancelled',
        [AppointmentStatus.SCHEDULED]: 'Pending',
        [AppointmentStatus.NULL]: null,
        [AppointmentStatus.UNKNOWN]: null,
      }

      const appointmentStatus = appointmentStatusMap[appointment.status]

      if (appointmentStatus !== null) {
        return appointmentStatus
      }

      if (appointment.cancellationReason) {
        return 'Cancelled'
      }
      const isPriorToToday = isBeforeToday(new Date(appointment.startTime))
      const hasConsultation = appointment.consultations.length > 0

      if (isPriorToToday && hasConsultation) {
        return 'Completed'
      }
      if (isPriorToToday && !hasConsultation) {
        return 'Not Completed'
      }

      if (hasConsultation) {
        return 'Attended'
      }

      return 'Pending'
    },
    [],
  )

  const rowsData =
    data?.map((appointment: AppointmentForPractitioner) => {
      const isCancelled = !!appointment.cancellationReason
      const currentStatus = calculateStatus(appointment)

      const menuItems = isCancelled
        ? []
        : [
            {
              label: t('Reschedule'),
              onClick: () =>
                setSelectedAppointment({
                  id: appointment.id,
                  facilityId: appointment.facility?.id ?? '',
                  startTime: appointment.startTime,
                  patientName: formatName(appointment.patient),
                  patientDob: appointment.patient.dateOfBirth ?? null,
                  department: appointment.department ?? null,
                  action: 'reschedule',
                  existingNote: appointment.statusNote,
                  currentStatus,
                }),
            },
            {
              label: t('Cancel'),
              onClick: () =>
                setSelectedAppointment({
                  id: appointment.id,
                  facilityId: appointment.facility?.id ?? '',
                  startTime: appointment.startTime,
                  patientName: formatName(appointment.patient),
                  patientDob: appointment.patient.dateOfBirth ?? null,
                  department: appointment.department ?? null,
                  action: 'cancel',
                  existingNote: appointment.statusNote,
                  currentStatus,
                }),
            },
            {
              label: t('Did Not Attend'),
              onClick: () =>
                setSelectedAppointment({
                  id: appointment.id,
                  facilityId: appointment.facility?.id ?? '',
                  startTime: appointment.startTime,
                  patientName: formatName(appointment.patient),
                  patientDob: appointment.patient.dateOfBirth ?? null,
                  department: appointment.department ?? null,
                  action: 'dna',
                  existingNote: appointment.statusNote,
                  currentStatus,
                }),
            },
          ]

      return {
        hasParsedData: false,
        columns: [
          new Date(appointment.startTime).toLocaleTimeString([], {
            hour: '2-digit',
            minute: '2-digit',
          }),
          formatName(appointment.patient),
          appointment.patient.dateOfBirth
            ? new Date(appointment.patient.dateOfBirth).toLocaleDateString()
            : appointment.patient.providedDateOfBirth || '',
          statusLabels[calculateStatus(appointment)],
          appointment.department ?? '',
        ],
        cellTextDecoration: isCancelled
          ? ({
              0: 'line-through',
              1: 'line-through',
            } as Record<number, 'line-through' | 'none' | 'underline'>)
          : undefined,
        onRowClick: () => {
          if (onRowSelected) onRowSelected(appointment)
        },
        actionComponent:
          menuItems.length > 0 ? (
            <TableCellIconButtonPopup menuItems={menuItems} />
          ) : null,
      }
    }) ?? []

  const headerData = {
    columns: [
      {title: t('Start Time'), columnWidthPercent: 10},
      {title: t('Patient Name'), columnWidthPercent: 22},
      {title: t('Date of Birth'), columnWidthPercent: 12},
      {title: t('Status'), columnWidthPercent: 15},
      {title: t('Location'), columnWidthPercent: 30},
      {title: '', columnWidthPercent: 11},
    ],
  }

  return (
    <>
      <Container direction="column" flex spacing={4}>
        <SimpleTable
          header={headerData}
          rows={rowsData}
          isLoading={isLoading}
        />
      </Container>
      {selectedAppointment && (
        <>
          <RescheduleAppointmentModal
            isVisible={selectedAppointment.action === 'reschedule'}
            onClose={() => setSelectedAppointment(null)}
            appointmentId={selectedAppointment.id}
            facilityId={selectedAppointment.facilityId}
            originalDate={selectedAppointment.startTime}
            patientName={selectedAppointment.patientName}
            patientDob={selectedAppointment.patientDob}
            department={selectedAppointment.department}
          />
          <CancelAppointmentModal
            isVisible={selectedAppointment.action === 'cancel'}
            onClose={() => setSelectedAppointment(null)}
            appointmentId={selectedAppointment.id}
            facilityId={selectedAppointment.facilityId}
            originalDate={selectedAppointment.startTime}
            patientName={selectedAppointment.patientName}
            patientDob={selectedAppointment.patientDob}
          />
          <DNAAppointmentModal
            isVisible={selectedAppointment.action === 'dna'}
            onClose={() => setSelectedAppointment(null)}
            appointmentId={selectedAppointment.id}
            facilityId={selectedAppointment.facilityId}
            originalDate={selectedAppointment.startTime}
            patientName={selectedAppointment.patientName}
            patientDob={selectedAppointment.patientDob}
            existingNote={selectedAppointment.existingNote}
            showWarning={['Completed', 'Attended'].includes(
              selectedAppointment.currentStatus ?? '',
            )}
          />
        </>
      )}
    </>
  )
}
